/** ***********************************************************************
 *
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe Systems Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 ************************************************************************* */

import { observer } from 'mobx-react';
import * as React from 'react';
import Button from '@react/react-spectrum/Button';
import Search from '@react/react-spectrum/Search';
import uuid from 'uuid';
import './layout.scss';
import ContentParser from '../../utils/contentParser';
import CCXUIAppConfig from '../../stores/config';

export default @observer class SearchBannerCommonLayout extends React.Component {
    renderGuid = uuid.v4();

    componentDidMount() {
        this.applyCustomCSS();
        const { bannerItem, appConfig, appsSkeletonModel } = this.props;
        const {
            analytics,
            urlList,
            textList,
            videoList,
            analytics: { bannerAnalyticsCallback },
            name,
            ingestDescription,
        } = bannerItem;
        bannerAnalyticsCallback('bannerRendered',
            {
                analytics,
                CTAText: textList[2],
                name,
                ingestDescription,
                renderGuid: this.renderGuid,
                ingestId: urlList[0] && urlList[0].ingestId,
                isVideo: videoList && videoList.length,
            },
            appConfig,
            appsSkeletonModel);
    }

    componentDidUpdate() {
        this.applyCustomCSS();
    }

    onSearch = (guid, urlList, onAction, bannerSectionId, bannerPlatform) => {
        let newUrl = urlList[0].url;
        if (this.searchedValue) {
            let encodedURI = this.searchedValue.trim();
            encodedURI = encodeURIComponent(encodedURI);
            newUrl = newUrl.replace('{search_input}', encodedURI);
        } else {
            newUrl = newUrl.replace('{search_input}', '');
        }
        onAction({ ...urlList[0], url: newUrl },
            guid, bannerSectionId, bannerPlatform);
        return newUrl;
    }

    applyCustomCSS() {
        const { quiet, bannerItem } = this.props;
        const [, , , placeHolder] = bannerItem.textList;
        if (quiet) {
            const searchIcon = document.querySelector('.search-banner-search .spectrum-UIIcon-Magnifier');
            if (searchIcon) {
                searchIcon.setAttribute('style', `color:${bannerItem.textColor}`);
            }
            const searchInput = document.querySelector('.search-banner-search .spectrum-Search-input');
            if (searchInput) {
                searchInput.setAttribute('style', `color:${bannerItem.textColor}; font-style: italic;`);
                searchInput.value = placeHolder;
                searchInput.addEventListener('focus', () => {
                    if (searchInput.value === placeHolder) {
                        searchInput.value = '';
                        searchInput.setAttribute('style', `color:${bannerItem.textColor}; font-style: normal;`);
                    }
                });
                searchInput.addEventListener('blur', () => {
                    if (searchInput.value === '') {
                        searchInput.value = placeHolder;
                        searchInput.setAttribute('style', `color:${bannerItem.textColor}; font-style: italic;`);
                    }
                });
            }
        }
    }

    renderLayout({
        imageList,
        videoList,
        textList,
        quiet,
        urlList,
        textColor,
        onAction,
        analytics,
        bannerSectionId,
        bannerPlatform,
        name,
        ingestDescription,
        appConfig,
        appsSkeletonModel,
    }) {
        const { bannerAnalyticsCallback } = analytics;

        const contentArray = [];

        const style = { color: textColor };
        if (videoList && videoList.length > 0) {
            let videoSourceList = [];
            const supportedFormats = ['ogg', 'webm', 'mp4'];
            videoList.forEach((video) => {
                videoSourceList = [];
                const gradientStyle = {
                    /* stylelint-disable */
                    backgroundImage: `linear-gradient(90deg, ${video.gradient || 'rgba(0,0,0,0.75)'}, hsla(0, 0%, 100%, 0))`,
                    /* stylelint-enable */
                };
                supportedFormats.forEach((format) => {
                    if (Object.prototype.hasOwnProperty.call(video, format)) {
                        videoSourceList.push({
                            type: `video/${format}`,
                            src: video[format],
                        });
                    }
                });
                const imageUrl = ContentParser.getAssetPath(video && video.thumbnailImage);

                contentArray.push(
                    <>
                    <div className={CCXUIAppConfig.isNonDownloadableSurface ? 'banner-background-video no-video' : 'banner-background-video'}>
                        <video
                            className="banner-video"
                            autoPlay
                            loop
                            muted
                            poster={imageUrl}
                            style={{ backgroundImage: `url(${imageUrl})` }}
                            playsInline
                            onPause={() => { bannerAnalyticsCallback('videoControlEvent', { analytics, type: 'pause' }, appConfig, appsSkeletonModel); }}
                            onPlay={
                                () => {
                                    bannerAnalyticsCallback('videoControlEvent', { analytics, type: 'resume' }, appConfig, appsSkeletonModel);
                                }
                            }>
                            {videoSourceList.map(
                                item => <source src={item.src} type={item.type} />
                            )}
                        </video>
                    </div>
                    <div className="banner-overlay" style={gradientStyle} />
                    </>
                );
            });
        } else if (!(videoList && videoList.length) && imageList && imageList.length > 0) {
            contentArray.push(<div className="catalog-banner-image" style={{ backgroundImage: `url('${imageList[0]}')` }} />);
        }
        if (textList && textList.length > 0) {
            const ele = (
                <div className="layout-content">
                    <div className="search-banner-product-info">
                        {!!imageList.length && (<img className="product-icon" src={imageList[1]} alt="Adobe Stock" />)}
                        <div className="title" style={style}>{textList[0]}</div>
                    </div>
                    <div className="search-banner-desc" style={style}>{textList[1]}</div>
                    <Search
                        className="search-banner-search"
                        placeholder={!quiet ? textList[3] : null}
                        quiet={quiet}
                        onChange={(searchedVal) => {
                            if (quiet) {
                                const clearButton = document.querySelector('.search-banner-search .spectrum-ClearButton');
                                if (clearButton) {
                                    const { bannerItem } = this.props;
                                    clearButton.setAttribute('style', `color:${bannerItem.textColor}`);
                                }
                            }
                            this.searchedValue = searchedVal;
                        }}
                        onSubmit={() => {
                            const guid = uuid.v4();
                            const newUrl = this.onSearch(
                                guid,
                                urlList,
                                onAction,
                                bannerSectionId,
                                bannerPlatform
                            );
                            bannerAnalyticsCallback('bannerCTAClicked',
                                {
                                    analytics: {
                                        ...analytics,
                                        guid,
                                        url: newUrl,
                                        uiSearchKeyword: this.searchedValue
                                                             && this.searchedValue.trim(),
                                    },
                                    name,
                                    ingestId: urlList[0] && urlList[0].ingestId,
                                    ingestDescription,
                                },
                                appConfig,
                                appsSkeletonModel);
                        }} />
                    {textList[2] && (
                        <Button
                            label={textList[2]}
                            role="button"
                            aria-label={textList[2]}
                            variant="primary"
                            className="action"
                            style={{ ...style, borderColor: textColor }}
                            onClick={() => {
                                const guid = uuid.v4();
                                const newUrl = this.onSearch(
                                    guid,
                                    urlList,
                                    onAction,
                                    bannerSectionId,
                                    bannerPlatform
                                );
                                bannerAnalyticsCallback('bannerCTAClicked',
                                    {
                                        analytics: {
                                            ...analytics,
                                            guid,
                                            url: newUrl,
                                            uiSearchKeyword: this.searchedValue
                                                             && this.searchedValue.trim(),
                                        },
                                        CTAText: textList[2],
                                        name,
                                        ingestId: urlList[0] && urlList[0].ingestId,
                                        ingestDescription,
                                    },
                                    appConfig,
                                    appsSkeletonModel);
                            }} />
                    )}
                </div>
            );
            contentArray.push(ele);
        }
        return contentArray;
    }

    render() {
        const {
            bannerItem,
            bannerSectionId,
            bannerPlatform,
            quiet,
            appConfig,
            appsSkeletonModel,
        } = this.props;
        const {
            imageList,
            videoList,
            textList,
            urlList,
            backgroundColor,
            textColor,
            onAction,
            analytics,
            name,
            ingestDescription,
        } = bannerItem;
        return (
            <div
                className="search-banner-item-layout"
                style={{ backgroundColor }}
                render-guid={this.renderGuid}
            >
                {this.renderLayout({
                    imageList,
                    videoList,
                    textList,
                    quiet,
                    urlList,
                    textColor,
                    onAction,
                    analytics,
                    bannerSectionId,
                    bannerPlatform,
                    name,
                    ingestDescription,
                    appConfig,
                    appsSkeletonModel,
                })}
            </div>
        );
    }
}
